import { useContext, useEffect, useState } from 'react'

import dayjs from 'dayjs'
import { FormikProps, FormikValues } from 'formik'
import { AppQuizUsageQuestion } from 'globalTypes'

import { useMutation } from '@apollo/client'
import PreTitle from 'Root/App/PreTitle'
import PageLinkWrapper from 'Root/App/Quiz/PageLinkWrapper'
import duration from 'dayjs/plugin/duration'
import filter from 'lodash/filter'
import join from 'lodash/join'
import max from 'lodash/max'
import size from 'lodash/size'

import { Context as StateContext } from '../../../StateContext'
import MUTATION from '../../../StateContext/app-quiz-state-context-storage.graphql'
import { getNextQuestion, hasNextQuestion } from '../../../utilities'
import Navigation from './Navigation'
import EssayType from './Type/Essay'
import MatchingType from './Type/Matching'
import MissingWordType from './Type/MissingWord'
import MultipleChoiceType from './Type/MultipleChoice'
import NumericalType from './Type/Numerical'
import SingleChoiceType from './Type/SingleChoice'
import TrueFalseType from './Type/TrueFalse'

interface Props {
  answer: any
  formikProps: FormikProps<FormikValues>
  index: number
  question: AppQuizUsageQuestion
}

const secondsToRelativeTime = (seconds?: number) => {
  if (!Boolean(seconds) || seconds <= 0) {
    return null
  }

  dayjs.extend(duration)

  return dayjs.duration(seconds, 'seconds').format('HH:mm:ss')
}

const Question = ({ index, question }: Props) => {
  const [remainingTime, setRemainingTime] = useState<number | null>(
    question.timeLimit
  )
  const { state, dispatch } = useContext(StateContext)
  const [mutate] = useMutation(MUTATION)

  useEffect(() => {
    if (!state.content.restartFromBegin) {
      const { content, ...stateWithoutContent } = state

      mutate({
        variables: {
          id: state.content.id,
          status: JSON.stringify(stateWithoutContent),
        },
      })
    }
  }, [state, mutate])

  useEffect(() => {
    if (!Boolean(question.timeLimit)) {
      return
    }

    const interval = setInterval(() => {
      const newValue = max([remainingTime - 10, 0])

      if (newValue === 0) {
        if (hasNextQuestion(state, question.id)) {
          const nextQuestionId = getNextQuestion(state, question.id).id

          dispatch({
            type: 'go-to-question',
            questionId: nextQuestionId,
          })
        }
      }

      setRemainingTime(newValue)
    }, 10000)

    return () => clearInterval(interval)
  }, [remainingTime, question, state, dispatch])

  const type = () => {
    switch (question.__typename) {
      case 'QuizContentQuestionEssay':
        return <EssayType question={question} />
      case 'QuizContentQuestionMatching':
        return <MatchingType question={question} />
      case 'QuizContentQuestionMissingWord':
        return <MissingWordType question={question} />
      case 'QuizContentQuestionMultipleChoice':
        return <MultipleChoiceType question={question} />
      case 'QuizContentQuestionNumerical':
        return <NumericalType question={question} />
      case 'QuizContentQuestionSingleChoice':
        return <SingleChoiceType question={question} />
      case 'QuizContentQuestionTrueFalse':
        return <TrueFalseType question={question} />
    }
  }

  const filtered = filter([
    state.content.label,
    state.content.showProgress
      ? `${index + 1}/${
          !!state.content.basket
            ? state.content.basket
            : size(state.content.questions)
        }`
      : null,
    secondsToRelativeTime(remainingTime || state.remainingTime),
  ])

  return (
    <div className="flex flex-col gap-y-3">
      <PageLinkWrapper slug={state.content.slug}>
        <PreTitle>{join(filtered, ' · ')}</PreTitle>
      </PageLinkWrapper>

      <div>{type()}</div>

      <Navigation question={question} />
    </div>
  )
}

export default Question
